export type SharePlatform =
  | "whatsapp"
  | "telegram"
  | "line"
  | "viber"
  | "messenger"
  | "wechat"
  | "kakao"
  | "system";

/**
 * Appends a per-minute cache-buster so messengers like WhatsApp re-scrape
 * the link preview instead of serving a stale cached card. We use a
 * minute-level stamp so legitimate retries within a minute don't waste
 * re-scrapes, while distinct sends (Vote, RSVP, RPE, etc.) each get a
 * fresh card.
 */
export function withCacheBuster(url: string): string {
  if (!url) return url;
  const stamp = Math.floor(Date.now() / 60000);
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${stamp}`;
}

/**
 * Builds a deep-link URL that opens the chosen messenger with the message
 * pre-filled and a native chat-picker. Returns empty string for `system`,
 * `wechat`, and `kakao` — the caller should fall back to clipboard copy.
 *
 * Notes:
 * - WhatsApp/Telegram/Line/Messenger all open a chat picker; you cannot
 *   deep-link directly into a specific private group from a third-party app.
 * - Viber's `viber://forward` scheme only works on devices with the app
 *   installed; desktop clicks are no-ops.
 * - WeChat and KakaoTalk don't expose a public URL share intent — we copy
 *   the message and toast the user to paste it in.
 */
export function buildShareIntentUrl(
  platform: SharePlatform,
  text: string,
  url: string,
): string {
  switch (platform) {
    case "whatsapp":
      return `https://wa.me/?text=${encodeURIComponent(text)}`;
    case "telegram":
      return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    case "line":
      return `https://line.me/R/msg/text/?${encodeURIComponent(text)}`;
    case "viber":
      return `viber://forward?text=${encodeURIComponent(text)}`;
    case "messenger":
      return `https://www.messenger.com/new?text=${encodeURIComponent(text)}`;
    case "wechat":
    case "kakao":
    case "system":
    default:
      return "";
  }
}

export function isValidGroupUrl(url: string): boolean {
  if (!url) return true;
  return /^https:\/\/(chat\.whatsapp\.com|wa\.me|t\.me|line\.me|invite\.viber\.com|viber\.com|m\.me|messenger\.com|weixin\.qq\.com|open\.kakao\.com)\//i.test(
    url.trim(),
  );
}

export const PLATFORM_LABELS: Record<SharePlatform, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  line: "LINE",
  viber: "Viber",
  messenger: "Messenger",
  wechat: "WeChat",
  kakao: "KakaoTalk",
  system: "System share sheet",
};

export const COPY_FALLBACK_PLATFORMS: SharePlatform[] = ["wechat", "kakao", "system"];

/**
 * Opens an external app/messenger intent without stranding the user on a
 * blank in-app browser page.
 *
 * In an installed PWA (especially iOS), `window.open(url, "_blank")` spawns an
 * in-app Safari view. When the URL immediately hands off to a native app
 * (wa.me -> whatsapp://, t.me -> tg://, viber://), that view has nothing left
 * to render and the user returns to a white "Search or enter website name"
 * page. Navigating the current window instead lets iOS perform the app
 * hand-off and bounce straight back to the PWA.
 */
export function openShareIntent(url: string): void {
  if (!url || typeof window === "undefined") return;

  let standalone = false;
  try {
    standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  } catch {
    standalone = false;
  }

  // Custom schemes never render a page — always hand them off in-place.
  const isAppScheme = /^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:/i.test(url);

  if (standalone || isAppScheme) {
    window.location.href = url;
    return;
  }

  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) window.location.href = url;
}
